import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import ItemDetail from '../../components/item-detail/index.gts';
import MentionCard from '../../components/mention-card.gts';
import MoreActionsMenu from '../../components/more-actions-menu.gts';
import ItemActions from '../../components/item-actions.gts';
import TypeBadge from '../../components/type-badge.gts';
import type { Item } from '../../schemas/item.ts';
import type { ItemStats } from '../../schemas/item-stats.ts';
import type { Mention } from '../../schemas/mention.ts';
import tw from '../../helpers/tw';
import pullToRefresh from '../../modifiers/pull-to-refresh.ts';
import type Session from '../../services/session';
import { fetchWatchRegion } from '../../utils/preferences';

interface Signature {
  Args: {
    model: {
      item: Item | null;
      stats: ItemStats | null;
      mentions: Mention[];
      myMentions: boolean;
      backQuery: {
        type: string;
        sort: string;
        q: string;
        page: string;
      };
    };
  };
}

export default class ItemsShow extends Component<Signature> {
  @service declare router: RouterService;
  @service declare session: Session;

  @tracked watchRegion = '';

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    void this.loadWatchRegion();
  }

  async loadWatchRegion() {
    this.watchRegion = await fetchWatchRegion(this.session.authHeader);
  }

  @action async refresh() {
    await this.router.refresh();
  }

  get enrichedTitle(): string {
    const item = this.args.model.item;
    if (!item) return '';

    const enriched = item.enrichedData as
      { title?: string; name?: string } | null | undefined;

    return enriched?.title || enriched?.name || item.name || '';
  }

  get itemEmoji(): string {
    const item = this.args.model.item;
    if (!item || item.type !== 'place') return '';
    return item.emoji ?? '';
  }

  get hasConsensus(): boolean {
    const count = this.args.model.stats?.opinionCount ?? 0;
    return count >= 3;
  }

  <template>
    <div {{pullToRefresh this.refresh}}>
      {{#if @model.item}}
        <div class="mb-4 flex items-center justify-between">
          {{#if @model.myMentions}}
            <LinkTo @route="items" class="btn btn-ghost btn-sm gap-1">
              &larr; Back to my items
            </LinkTo>
          {{else}}
            <LinkTo
              @route="explore.index.results"
              @query={{hash
                type=@model.backQuery.type
                sort=@model.backQuery.sort
                q=@model.backQuery.q
                page=@model.backQuery.page
              }}
              class="btn btn-ghost btn-sm gap-1"
            >
              &larr; Back to items
            </LinkTo>
          {{/if}}
          <MoreActionsMenu @targetType="item" @targetId={{@model.item.id}} />
        </div>

        {{! Header: name + badges }}
        <div class="bg-base-100 rounded-box mb-4 p-6 shadow">
          <h1 class="mb-2 text-3xl font-bold">
            {{#if this.itemEmoji}}
              <span aria-hidden="true">{{this.itemEmoji}}</span>
            {{/if}}
            {{this.enrichedTitle}}
          </h1>
          <div class="flex flex-wrap gap-2">
            <TypeBadge @type={{@model.item.type}} />
            {{#if @model.item.language}}
              <span class="badge badge-outline">
                {{@model.item.language}}
              </span>
            {{/if}}
            {{#if @model.stats.opinionCount}}
              <span class="badge badge-ghost" title="Opinion mentions">
                {{@model.stats.opinionCount}}
                opinions
              </span>
            {{/if}}
            {{#if @model.stats.contextualCount}}
              <span
                class="badge badge-ghost badge-outline"
                title="Contextual references"
              >
                {{@model.stats.contextualCount}}
                references
              </span>
            {{/if}}
            {{#if this.hasConsensus}}
              <span
                class={{tw "badge" @model.stats.sentimentClass}}
                title="Recommendation consensus (opinions only)"
              >
                🍅
                {{@model.stats.recommendationPct}}%
              </span>
            {{/if}}
          </div>

          <div class="mt-4 flex gap-2">
            <ItemActions @itemId={{@model.item.id}} />
          </div>
        </div>

        {{! Enriched detail section }}
        <ItemDetail @item={{@model.item}} @watchRegion={{this.watchRegion}} />

        {{! Mentions list }}
        <h2 class="mt-8 mb-4 text-2xl font-bold">Mentions</h2>
        {{#if @model.myMentions}}
          <p class="text-base-content/60 -mt-2 mb-4 text-sm">
            Showing mentions from your sources only.
          </p>
        {{/if}}

        {{#if @model.mentions.length}}
          <div class="flex flex-col gap-4">
            {{#each @model.mentions as |mention|}}
              <MentionCard @mention={{mention}} @hideItem={{true}} />
            {{/each}}
          </div>
        {{else}}
          <div class="alert">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              class="h-6 w-6 shrink-0 stroke-current"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span>No mentions found for this item.</span>
          </div>
        {{/if}}
      {{else}}
        <div class="alert alert-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <span>Item not found</span>
        </div>
      {{/if}}
    </div>
  </template>
}
